import {
  ExperimentTemplate,
  ExperimentTemplateData
} from '@experiment-details/types';
import useExpDetailsSelectedTemplate from '../useExpDetailsSelectedTemplate';
import { useCallback } from 'react';
import { useExpDetailsTemplateAPIContext } from './useExpDetailsTemplateAPIContext';
import { useQueryClient } from 'react-query';
import { expDetailsTemplateKeys } from '../api/exp-details-template-key-factory';
import useDuplicateExpDetailsTemplateMutation from './useDuplicateExpDetailsTemplateMutation';
import { debounce } from 'lodash';
import { AUTO_GENERATED_OPTION_ID } from '@shared/constants';
import useExpDetailsRouteParams from '../useExpDetailsRouteParams';
import useChangeWorkspaceViewMessage from '@shared/hooks/useChangeWorkspaceViewMessage';

function getUpdatedTemplate(
  template: ExperimentTemplateData,
  data: Partial<ExperimentTemplate>
): ExperimentTemplateData {
  const chartTemplate: ExperimentTemplate = { ...template.chartTemplate };

  (Object.keys(data) as Array<keyof ExperimentTemplate>).forEach(key => {
    const sourceValue = data[key];
    const targetValue = chartTemplate[key];

    if (sourceValue !== undefined) {
      if (
        typeof sourceValue === 'object' &&
        typeof targetValue === 'object' &&
        !Array.isArray(sourceValue)
      ) {
        // @ts-expect-error ts old version issue
        chartTemplate[key] = {
          ...targetValue,
          ...sourceValue
        };
      } else {
        // @ts-expect-error ts old version issue
        chartTemplate[key] = sourceValue;
      }
    }
  });

  return {
    ...template,
    chartTemplate
  };
}

const useUpdateSelectedTemplate = () => {
  const queryClient = useQueryClient();
  const { templateData } = useExpDetailsSelectedTemplate();
  const { experimentKey, projectId } = useExpDetailsRouteParams();

  const {
    saveTemplateLocally,
    saveTemplateOnServerDebounced
  } = useExpDetailsTemplateAPIContext();
  const duplicateTemplateMutation = useDuplicateExpDetailsTemplateMutation();
  const debouncedDuplicateView = debounce(
    duplicateTemplateMutation.mutate,
    1000
  );
  const showChangeWorkspaceViewMessage = useChangeWorkspaceViewMessage();

  const onUpdateTemplateState = (updatedData: Partial<ExperimentTemplate>) => {
    if (!templateData || duplicateTemplateMutation.isLoading) return;

    const isPersonalTemplate = templateData.personalView;

    // Personal template
    if (isPersonalTemplate) {
      // get fresh Personal template data
      const currentPersonalTemplate = queryClient.getQueryData(
        expDetailsTemplateKeys.personalTemplate(experimentKey, projectId)
      ) as ExperimentTemplateData;

      const updatedTemplate = getUpdatedTemplate(
        currentPersonalTemplate,
        updatedData
      );

      queryClient.setQueryData(
        expDetailsTemplateKeys.personalTemplate(experimentKey, projectId),
        updatedTemplate
      );

      // Immediately save Default Personal template
      if (updatedTemplate.templateId === AUTO_GENERATED_OPTION_ID) {
        debouncedDuplicateView(
          {
            templateData: updatedTemplate
          },
          {
            onSuccess: data => {
              queryClient.setQueryData(
                expDetailsTemplateKeys.personalTemplate(
                  experimentKey,
                  projectId
                ),
                data
              );
            }
          }
        );

        return;
      }

      saveTemplateLocally(updatedTemplate);
      saveTemplateOnServerDebounced(updatedTemplate);

      return;
    }

    // Saved workspace template
    const templateId = templateData.templateId as string;
    // get fresh Project template data
    const currentProjectTemplate = queryClient.getQueryData(
      expDetailsTemplateKeys.projectTemplate(templateId)
    ) as ExperimentTemplateData;

    const updatedTemplate = getUpdatedTemplate(
      currentProjectTemplate,
      updatedData
    );

    const isTemplateFromOtherProject = projectId !== updatedTemplate.projectId;

    if (isTemplateFromOtherProject) {
      showChangeWorkspaceViewMessage(updatedTemplate.templateName);
      return;
    }

    queryClient.setQueryData(
      expDetailsTemplateKeys.projectTemplate(templateId),
      updatedTemplate
    );

    saveTemplateLocally(updatedTemplate);
    saveTemplateOnServerDebounced(updatedTemplate);
  };

  // keep less deps as possible
  return useCallback(onUpdateTemplateState, [
    templateData?.templateId,
    templateData?.personalView,
    projectId,
    experimentKey
  ]);
};

export default useUpdateSelectedTemplate;
